import React, { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import EmailCaptureModal from './EmailCaptureModal';
import { supabase } from '../lib/supabase';

interface BlogDownloadSectionProps {
  title: string;
  description: string;
  buttonText: string;
  pdfName: string;
  source: string;
  className?: string;
}

export default function BlogDownloadSection({
  title = "Free Resource",
  description = "Get our comprehensive resource to help you implement these strategies effectively.",
  buttonText = "Download PDF",
  pdfName,
  source,
  className = "my-6 bg-gray-50 rounded-lg border border-gray-200"
}: BlogDownloadSectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Fetch the PDF URL when the component mounts
  useEffect(() => {
    const fetchPdfUrl = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('uploads')
          .select('url')
          .eq('name', pdfName)
          .eq('deleted', false)
          .single();
        
        if (error) {
          console.error('Error fetching PDF URL:', error);
          // Fallback to the static file if database fetch fails
          setPdfUrl(`/resources/${pdfName}`);
        } else if (data) {
          console.log('Found PDF in uploads table:', data);
          setPdfUrl(data.url);
        } else {
          console.warn('PDF not found in uploads table, using fallback');
          setPdfUrl(`/resources/${pdfName}`);
        }
      } catch (err) {
        console.error('Error in fetchPdfUrl:', err);
        setPdfUrl(`/resources/${pdfName}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPdfUrl();
  }, [pdfName]);

  const handleDownloadClick = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <div className={className}>
        <div className="px-5 py-4">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600 mb-3">
            {description}
          </p>
          <button 
            onClick={handleDownloadClick} 
            className="inline-flex items-center px-4 py-2 bg-[#F98B3D] text-white rounded-md hover:bg-[#e07a2c] transition-colors duration-200"
            disabled={loading}
          >
            <Download className="h-5 w-5 mr-2" />
            {buttonText}
          </button>
        </div>
      </div>
      
      {pdfUrl && (
        <EmailCaptureModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          downloadUrl={pdfUrl}
          source={source}
          title="Download Your Free Resource"
          description="Please enter your name and email to receive this resource. We respect your privacy and will never share your information."
        />
      )}
    </>
  );
}